import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';

/**
 * Header du site — version standalone pour les pages News et Article.
 * Les liens anchor redirigent vers la page d'accueil (#section).
 * Inclut le switch de langue EN/FR (persisté dans localStorage).
 */
const SiteHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem('lang') || 'en');

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = () => setMobileMenuOpen(false);

  const switchLang = (l) => {
    localStorage.setItem('lang', l);
    setLang(l);
    // Reload so News/BlogArticle pick up the new language
    window.location.reload();
  };

  return (
    <>
      <header className={`header ${isScrolled ? 'scrolled' : ''}`} style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
        <div className="container">
          <div className="header-content">

            {/* Logo */}
            <Link to="/" className="logo">
              <img
                src="/logo.png"
                alt="ANPMN Logo"
                className="logo-image"
                loading="eager"
              />
            </Link>

            {/* Navigation desktop */}
            <nav className="nav">
              <div className="nav-dropdown">
                <a href="/#apropos" className="nav-dropdown-toggle">About us</a>
                <div className="nav-dropdown-menu">
                  <a href="/#apropos">Our Mission</a>
                  <a href="/#apropos">Our Vision</a>
                </div>
              </div>

              <div className="nav-dropdown">
                <a href="/#equipe" className="nav-dropdown-toggle">Organization</a>
                <div className="nav-dropdown-menu">
                  <a href="/#equipe">Members</a>
                  <a href="/#steering-committee-section">Steering Committee</a>
                </div>
              </div>

              <div className="nav-dropdown">
                <a href="/#partners" className="nav-dropdown-toggle">Partners</a>
                <div className="nav-dropdown-menu">
                  <a href="https://www.un.org/en/" target="_blank" rel="noopener noreferrer">United Nations</a>
                  <a href="https://www.ohchr.org/en/treaty-bodies/spt" target="_blank" rel="noopener noreferrer">SPT</a>
                  <a href="https://www.coe.int/en/web/cpt" target="_blank" rel="noopener noreferrer">CPT</a>
                </div>
              </div>

              <a href="/#marrakech-event-section">Founding documents</a>
              <Link to="/news">News &amp; Events</Link>
            </nav>

            {/* Actions */}
            <div className="header-actions">
              <a href="/#secretariat-section" className="header-btn">
                <MapPin size={18} />
                <span>Permanent Secretariat</span>
              </a>

              {/* Language switch desktop */}
              <div className="lang-switch">
                <button
                  className={`lang-btn ${lang === 'en' ? 'active' : ''}`}
                  onClick={() => switchLang('en')}
                >EN</button>
                <span className="lang-sep">|</span>
                <button
                  className={`lang-btn ${lang === 'fr' ? 'active' : ''}`}
                  onClick={() => switchLang('fr')}
                >FR</button>
              </div>

              {/* Bouton Hamburger Mobile */}
              <button
                className={`mobile-menu-btn ${mobileMenuOpen ? 'open' : ''}`}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>

          </div>
        </div>
      </header>

      {/* Menu Mobile Overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={closeMenu} />
      <div className={`mobile-menu-drawer ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <img src="/logo.png" alt="ANPMN Logo" className="mobile-menu-logo" />
          <button className="mobile-menu-close" onClick={closeMenu} aria-label="Fermer le menu">✕</button>
        </div>
        <nav className="mobile-menu-nav">
          <div className="mobile-menu-group">
            <span className="mobile-menu-group-label">About us</span>
            <a href="/#apropos" className="mobile-menu-link" onClick={closeMenu}>Our Mission</a>
            <a href="/#apropos" className="mobile-menu-link" onClick={closeMenu}>Our Vision</a>
          </div>
          <div className="mobile-menu-group">
            <span className="mobile-menu-group-label">Organization</span>
            <a href="/#equipe" className="mobile-menu-link" onClick={closeMenu}>Members</a>
            <a href="/#steering-committee-section" className="mobile-menu-link" onClick={closeMenu}>Steering Committee</a>
          </div>
          <div className="mobile-menu-group">
            <span className="mobile-menu-group-label">Partners</span>
            <a href="https://www.un.org/en/" target="_blank" rel="noopener noreferrer" className="mobile-menu-link" onClick={closeMenu}>United Nations</a>
            <a href="https://www.ohchr.org/en/treaty-bodies/spt" target="_blank" rel="noopener noreferrer" className="mobile-menu-link" onClick={closeMenu}>SPT</a>
            <a href="https://www.coe.int/en/web/cpt" target="_blank" rel="noopener noreferrer" className="mobile-menu-link" onClick={closeMenu}>CPT</a>
          </div>
          <a href="/#marrakech-event-section" className="mobile-menu-link mobile-menu-link--standalone" onClick={closeMenu}>Founding documents</a>
          <Link to="/news" className="mobile-menu-link mobile-menu-link--standalone" onClick={closeMenu}>News &amp; Events</Link>
          <a href="/#secretariat-section" className="mobile-menu-secretariat" onClick={closeMenu}>
            <MapPin size={16} />
            Permanent Secretariat
          </a>

          {/* Language switch in mobile drawer */}
          <div className="mobile-menu-lang">
            <button
              className={`mobile-lang-btn ${lang === 'en' ? 'active' : ''}`}
              onClick={() => { switchLang('en'); closeMenu(); }}
            >EN</button>
            <span className="mobile-lang-sep">|</span>
            <button
              className={`mobile-lang-btn ${lang === 'fr' ? 'active' : ''}`}
              onClick={() => { switchLang('fr'); closeMenu(); }}
            >FR</button>
          </div>
        </nav>
      </div>
    </>
  );
};

export default SiteHeader;
